<template>
  <div class="content-wrapper builder-page" style="min-height: calc(100vh - 101px);">
    <!-- Content Header (Page header) -->
    <header class="builder-header">
      <div class="builder-header__title">
        <span class="builder-header__icon"><i class="fa fa-comments-o" aria-hidden="true"></i></span>
        <div class="builder-header__text">
          <ol class="builder-crumbs">
            <li><router-link to="/conversation/view">Conversations</router-link></li>
            <li>{{labelName}}</li>
          </ol>
          <h1>{{ currentJourneyData.name || labelName }}</h1>
        </div>
        <span class="builder-chip" :class="{ 'builder-chip--edit': isEdit }">{{ isEdit ? 'Editing' : 'New' }}</span>
      </div>
    </header>
    <!-- Main content -->
    <section class="builder-body">
              <div class="journey-main-container">
                  <conversation-tree-view 
                    :conversationObject="currentJourneyData" 
                    :isJourneyConversation="false" 
                    :selectedStageName="`${selectedStage ? selectedStage.stageName : 'uninitialized'}`"
                    v-on:nodeClick="nodeClicked"
                    v-on:editStage="handleEditStageClick({ item: $event })" 
                    v-on:validateConversation="validateConversation"/>
                  <div class="resizer" id="resizeDragger" title="Drag to resize"><span class="resizer__grip"></span></div>
                  <div class="container__right">
                    <div v-if="stageLoading" class="stage-switching">
                      <span class="stage-switching__pill"><i class="fa fa-circle-o-notch fa-spin" aria-hidden="true"></i> Loading stage…</span>
                    </div>
                    <conversation
                      ref='conversation' 
                      :stageToEdit="selectedStage" 
                      :isJourneyConversation="false" 
                      :metaData="metaData"
                      :config="config"
                      :folderName="folderName"
                      :conversationName="currentJourneyData.name"
                      :buttonDialog="buttonDialog"
                      :isStageEdited="isStageEdited"
                      :allConversations="allConversations"
                      v-on:updateConfirmingSaveStage="updateConfirmingSaveStage"
                      v-on:stageReady="stageLoaded"
                      v-on:updateTree="updateTree"/>
                  </div>
              </div>
    </section>

    <vue-simple-context-menu
      :elementId="'contextMenu'"
      :options="options"
      :ref="'treeContextMenu'"
      @option-clicked="optionClicked"
    />
    <!-- /.content -->
    <BlockUI v-if="processing"><i class="fa fa-cog fa-spin fa-3x fa-fw"></i><br/>Please wait...</BlockUI>
  </div>
</template>
<script>
import VueSimpleContextMenu from 'vue-simple-context-menu'
import { isEqual, escape } from 'lodash'
import api from '../../../utils/api'
import { askToUndo } from '../shared/undoCountdown'
import { nextPaint } from '../shared/nextPaint'
import TreeView from '../shared/conversationTreeView.vue'
import Conversation from '../shared/conversationPanel.vue'
import { stageTypes } from './constant'
import { domain } from '../../../config/constants'

export default {
  name: 'create-or-update-conversation',
  components: {
    'conversation': Conversation,
    'conversation-tree-view': TreeView,
    'vue-simple-context-menu': VueSimpleContextMenu
  },
  data () {
    const defaultUninitializedStage = {
      stageName: 'uninitialized'
    }

    return {
      processing: false,
      currentJourneyData: {},
      selectedStage: {...defaultUninitializedStage},
      stages: {defaultUninitializedStage},
      metaData: {
        snomed_codes: [],
        funnel_stages: [],
        conv_theme: {},
        is_responsible_for_referrals: false,
        is_new_conversation: !this.$route.query.app
      },
      config: {
        name: '',
        description: '',
        type: 'others',
        stages: {}
      },
      options: [
        {
          name: '<i class="fa fa-pencil"></i> Edit',
          slug: 'edit'
        },
        {
          name: '<i class="fa fa-trash-o"></i> Delete',
          slug: 'delete',
          class: 'nv-menu-danger'
        }
      ],
      labelName: 'Create A Conversation',
      isConfirmingSaveStageOnRouteChange: true,
      isSwitchingStage: false,
      stageLoading: false,
      editStageName: '',
      allConversations: [],
      folderName: ''
    }
  },
  computed: {
    isEdit () {
      return !!this.$route.query.app
    }
  },
  async mounted () {
    // collapse sidebar to view full screen
    document.body.classList.add('sidebar-collapse')
    this.getDraggableResizer()
    // awaiting for proper reflection in update convo page
    if (this.$route.query.app) {
      this.labelName = 'Edit A Conversation'
      this.retrieveAnApp(this.$route.query.app)
    }
  },
  methods: {
    updateTree (conversation, savedStageName) {
      this.currentJourneyData = conversation
      this.metaData = conversation.metaData
      this.selectedStage = conversation.config.stages[savedStageName]
      this.stages = conversation.config.stages
    },
    nodeClicked (stageName) {
      this.selectedStage = {...this.stages[stageName], stageName}
    },
    validateConversation () {
      this.$refs.conversation.saveStage(false, null, true)
    },
    async handleEditStageClick (event) {
      const stageName = event.item
      // This will be used at 2 places: isStageEdited and after saving stage, updating the selectedStage with this name
      this.editStageName = stageName
      // Show "Loading stage…" straight away; checking for unsaved changes can take a moment too
      this.stageLoading = true
      await nextPaint()
      const isStageEdited = await this.isStageEdited()
      if (isStageEdited) {
        this.stageLoading = false
        this.isSwitchingStage = true
        const result = await this.buttonDialog('You have unsaved stage. Do you want to save the changes?', 'Save', `Don't save`)
        if (result.dismiss && result.dismiss === 'cancel') {
          await this.switchStage(stageName, true)
          this.isSwitchingStage = false
        } else {
          this.$refs.conversation.saveStage(false, null)
        }
      } else {
        await this.switchStage(stageName, true)
      }
    },
    // Opens a stage in the right-hand panel behind a "Loading stage…" overlay. The overlay is painted
    // before the switch starts, because rendering the stage form blocks the page for a moment.
    async switchStage (stageName, refreshMetaData = false) {
      this.stageLoading = true
      await nextPaint()
      this.selectedStage = {...this.stages[stageName], stageName}
      if (refreshMetaData) this.metaData = {...this.metaData}
      // In case the stage form never reports back
      clearTimeout(this.stageLoadingTimer)
      this.stageLoadingTimer = setTimeout(() => { this.stageLoading = false }, 4000)
    },
    stageLoaded () {
      clearTimeout(this.stageLoadingTimer)
      this.stageLoading = false
    },
    async handleDeleteStageClick (event) {
      if (event.item === 'uninitialized') {
        this.$swal({
          title: 'Cannot delete uninitialized stage',
          type: 'error'
        })
      } else if (this.currentJourneyData.config.stages.hasOwnProperty(event.item)) {
        const result = await this.buttonDialog(`Are you sure you want to delete stage?`, 'Yes', 'No', {
          htmlText: `<b style="font-size: large">${event.item}</b>`,
          width: 340,
          danger: true
        })
        if (!result.dismiss) {
          const stageName = event.item
          // Kept so the deletion can be undone
          const deletedStage = JSON.parse(JSON.stringify(this.currentJourneyData.config.stages[stageName]))
          delete this.currentJourneyData.config.stages[stageName]
          delete this.stages[stageName]
          delete this.config.stages[stageName]
          const deleted = await this.stageDelete()
          const currentStage = await this.$refs.conversation.getCurrentStageData()
          if (currentStage.stageName === stageName) {
            this.selectedStage = {...this.stages['uninitialized'], stageName: 'uninitialized'}
          }
          // Redraw without the stage now, not after the undo popup closes
          this.currentJourneyData = JSON.parse(JSON.stringify(this.currentJourneyData))
          if (deleted) {
            const undo = await askToUndo(this.$swal, {
              title: 'Stage deleted',
              html: `<b>${escape(stageName)}</b> was deleted. Remember to remove links to it from other stages.`
            })
            if (undo) await this.restoreStage(stageName, deletedStage)
          }
        }
      } else {
        this.$swal({
          title: 'Stage does not exist',
          type: 'info',
          text: 'Please remove the reference from the parent stage to hide the stage'
        })
      }
      this.currentJourneyData = JSON.parse(JSON.stringify(this.currentJourneyData))
    },
    async optionClicked (event) {
      switch (event.option.slug) {
        case 'edit': this.handleEditStageClick(event)
          break
        case 'delete': this.handleDeleteStageClick(event)
          break
      }
    },
    // Saves the journey without the deleted stage. Returns true if it saved; the caller offers Undo.
    async stageDelete () {
      try {
        this.processing = true
        const response = await api.buildRequest('app', 'POST', { ...this.currentJourneyData, saveStage: true })
        this.processing = false
        if (response.status === 200) return true
        this.$swal({
          title: 'Stage deleted successfully',
          type: 'error',
          text: 'Please remove the reference from the parent stage to hide the stage'
        })
        return false
      } catch (error) {
        this.$swal({
          title: 'Delete Stage',
          type: 'error',
          html: error.response.data.payload.message
        })
        this.processing = false
        return false
      }
    },
    // Undo for a deleted stage: put it back and save the journey again
    async restoreStage (stageName, stage) {
      this.currentJourneyData.config.stages[stageName] = JSON.parse(JSON.stringify(stage))
      this.$set(this.stages, stageName, JSON.parse(JSON.stringify(stage)))
      this.$set(this.config.stages, stageName, JSON.parse(JSON.stringify(stage)))
      try {
        this.processing = true
        const response = await api.buildRequest('app', 'POST', { ...this.currentJourneyData, saveStage: true })
        this.processing = false
        if (response.status === 200) {
          this.$swal({ title: 'Stage restored', type: 'success' })
        } else {
          this.$swal({ title: 'Couldn\'t restore the stage', type: 'error', text: 'It\'s back in the editor but not saved. Save the stage to keep it.' })
        }
      } catch (error) {
        this.processing = false
        this.$swal({
          title: 'Couldn\'t restore the stage',
          type: 'error',
          html: (error.response && error.response.data.payload.message) || 'It\'s back in the editor but not saved. Save the stage to keep it.'
        })
      }
      this.currentJourneyData = JSON.parse(JSON.stringify(this.currentJourneyData))
    },
    retrieveAnApp (appId) {
      this.processing = true
      api.buildRequest(`app/${appId}`, 'GET')
        .then(response => {
          if (response.status === 200) {
            this.processing = false
            this.currentJourneyData = response.data.payload.details
            this.config = response.data.payload.details.config
            this.folderName = response.data.payload.details.folderName
            this.metaData = {...this.metaData, ...response.data.payload.details.metaData}
            const stagesObject = {...response.data.payload.details.config.stages}
            this.stages = stagesObject
            var domainname = window.location.hostname
            const country = (this.env && this.env.country) ? this.env.country : domain[domainname].defaultConversationCountry
            if (response.data.payload.details.__v === 0) {
              var stages = this.stages
              for (var stageObj in stages) {
                var textCountry = []
                stages[stageObj].text.forEach(function (messageSet) {
                  var textCountryObj = {}
                  var defaultCountry = 'Global'
                  textCountryObj[country] = {}
                  textCountryObj[defaultCountry] = {}
                  for (var lang in messageSet) {
                    if (lang === 'properties') {
                      textCountryObj[lang] = messageSet[lang]
                    } else {
                      textCountryObj[country][lang] = messageSet[lang]
                      if (lang === 'eng') {
                        textCountryObj[defaultCountry][lang] = messageSet[lang]
                      }
                    }
                  }
                  textCountry.push(textCountryObj)
                  stages[stageObj].text = textCountry
                })
              }
              this.stages = stages
            }

            if (this.stages['uninitialized']) {
              this.selectedStage = {...this.stages['uninitialized'], stageName: 'uninitialized'}
            }
          } else {
            this.processing = false
          }
        })
    },
    getDraggableResizer () {
      // Query the element
      const resizer = document.getElementById('resizeDragger')
      const leftSide = resizer.previousElementSibling
      const rightSide = resizer.nextElementSibling

      // The current position of mouse
      let x = 0
      let leftWidth = 0
      // Handle the mousedown event
      // that's triggered when user drags the resizer
      const mouseDownHandler = function (e) {
          // Get the current mouse position
        x = e.clientX
        leftWidth = leftSide.getBoundingClientRect().width

        // Attach the listeners to `document`
        document.addEventListener('mousemove', mouseMoveHandler)
        document.addEventListener('mouseup', mouseUpHandler)
      }

      const mouseMoveHandler = function (e) {
          // How far the mouse has been moved
        const dx = e.clientX - x

        const newLeftWidth = ((leftWidth + dx) * 100) / resizer.parentNode.getBoundingClientRect().width
        leftSide.style.width = `${newLeftWidth}%`

        resizer.style.cursor = 'col-resize'
        document.body.style.cursor = 'col-resize'

        leftSide.style.userSelect = 'none'
        leftSide.style.pointerEvents = 'none'

        rightSide.style.userSelect = 'none'
        rightSide.style.pointerEvents = 'none'
      }

      const mouseUpHandler = function () {
        resizer.style.removeProperty('cursor')
        document.body.style.removeProperty('cursor')

        leftSide.style.removeProperty('user-select')
        leftSide.style.removeProperty('pointer-events')

        rightSide.style.removeProperty('user-select')
        rightSide.style.removeProperty('pointer-events')

        // Remove the handlers of `mousemove` and `mouseup`
        document.removeEventListener('mousemove', mouseMoveHandler)
        document.removeEventListener('mouseup', mouseUpHandler)
      }

      // Attach the handler
      resizer.addEventListener('mousedown', mouseDownHandler)
    },
    async isStageEdited () {
      const isMessageSetEdited = await this.isMessageSetEdited()
      if (isMessageSetEdited) return true

      const currentStage = await this.$refs.conversation.getCurrentStageData()
      if (this.isMetadataEdited(currentStage)) return true
      if (this.isStageSettingsEdited(currentStage)) return true
      return false
    },
    isMessageSetEdited () {
      return this.$refs.conversation.isMessageSetEdited()
    },
    isMetadataEdited (currentStage) {
      if (currentStage.stageName === 'uninitialized') {
        if (!isEqual(this.metaData, this.$refs.conversation.getConversationMetadata())) return true
      }
      return false
    },
    isStageSettingsEdited (currentStage) {
      let configStage = {}
      // For stages present in option but not in journey. This would be helpful in generating an unsaved default stage if config.stages[stageName] is empty
      const defaultStageFields = {
        mediaType: 'text',
        stageType: stageTypes.traditional,
        recommendation: [],
        text: [],
        semantic: []
      }
      // This is to handle condition of adding default fields only if current stage is traditional because that would be default state of stage which is not saved
      if (currentStage['stageType'] === stageTypes.traditional) {
        configStage = { ...defaultStageFields }
      }
      const stageName = currentStage['stageName']
      configStage = { ...configStage, ...this.config.stages[stageName], stageName: this.selectedStage.stageName }

      // If it was only an option and no stage, we simply add stage name to stop inequality generated
      if (!currentStage.length) currentStage = {...currentStage, stageName: this.selectedStage.stageName}
      delete configStage.incoming
      delete configStage.outgoing
      delete configStage.plottedIncoming
      return !isEqual(JSON.parse(JSON.stringify(currentStage)), JSON.parse(JSON.stringify(configStage)))
    },
    async buttonDialog (title, confirmButtonText, cancelButtonText, { htmlText = null, width = null, danger = false } = {}) {
      return this.$swal({
        title,
        showCancelButton: true,
        confirmButtonText,
        cancelButtonText,
        allowOutsideClick: false,
        allowEscapeKey: false,
        ...htmlText && { html: htmlText },
        ...width && { width: width },
        // Destructive action: red confirm button, and Enter picks the safe choice
        ...danger && { type: 'warning', confirmButtonColor: '#E05263', focusCancel: true }
      })
    },
    updateConfirmingSaveStage (isConfirmingSaveStageOnRouteChange) {
      if (this.isSwitchingStage) {
        this.isSwitchingStage = false
        this.switchStage(this.editStageName)
      } else {
        this.isConfirmingSaveStageOnRouteChange = isConfirmingSaveStageOnRouteChange
      }
    },
    async getConfirmationIfStageEdited (nextTargetPath) {
      const isStageEdited = await this.isStageEdited()
      if (isStageEdited) {
        const result = await this.buttonDialog('You have unsaved stage. Do you want to save the changes?', 'Save', `Don't save`)
        if (result.dismiss && result.dismiss === 'cancel') {
          this.isConfirmingSaveStageOnRouteChange = false
          this.$router.push({path: nextTargetPath})
        } else {
          this.$refs.conversation.saveStage(true, nextTargetPath)
        }
      } else {
        this.isConfirmingSaveStageOnRouteChange = false
        this.$router.push({path: nextTargetPath})
      }
    }
  },
  beforeRouteLeave (to, from, next) {
    if (!this.isConfirmingSaveStageOnRouteChange) {
      next()
    } else {
      this.getConfirmationIfStageEdited(to.path)
      next(false)
    }
  }
}
</script>
<style scoped>
  .builder-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 16px;
      padding: 16px 24px 12px;
  }
  .builder-header__title {
      display: flex;
      align-items: center;
      gap: 12px;
      min-width: 0;
  }
  .builder-header__icon {
      flex-shrink: 0;
      width: 40px;
      height: 40px;
      border-radius: 12px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      background: linear-gradient(135deg, #1E3A5F 0%, #0D9488 100%);
      color: #FFFFFF;
      font-size: 18px;
  }
  .builder-header__text {
      min-width: 0;
  }
  .builder-header h1 {
      margin: 0;
      font-size: 20px;
      font-weight: 700;
      color: #1E3A5F;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
  }
  .builder-crumbs {
      display: flex;
      margin: 0 0 2px;
      padding: 0;
      list-style: none;
      font-size: 12px;
      color: #8896A5;
  }
  .builder-crumbs li + li::before {
      content: '/';
      margin: 0 6px;
  }
  .builder-crumbs a {
      color: #5C6E8A;
  }
  .builder-crumbs a:hover {
      color: #0D9488;
  }
  .builder-chip {
      flex-shrink: 0;
      padding: 3px 10px;
      border-radius: 999px;
      background: #E6F5F4;
      color: #0B7C72;
      font-size: 12px;
      font-weight: 700;
  }
  .builder-chip--edit {
      background: #EBF0F7;
      color: #1E3A5F;
  }
  .builder-body {
      padding: 0 24px 20px;
  }
  .journey-main-container {
      display: flex;
      height: calc(100vh - 170px);
      min-height: 520px;
      width: 100%;
      overflow: hidden;
      border: 1px solid #E2E8F0;
      border-radius: 12px;
      background: #FFFFFF;
      box-shadow: 0 4px 20px -4px rgba(29, 40, 58, 0.08);
  }
  .container__left {
      width: 60%;
      display: flex;
      overflow: auto;
  }
  .resizer {
      flex-shrink: 0;
      position: relative;
      width: 8px;
      height: 100%;
      cursor: col-resize;
      background: #F5F7FA;
      border-left: 1px solid #E2E8F0;
      border-right: 1px solid #E2E8F0;
      transition: background-color 0.15s ease;
  }
  .resizer:hover {
      background: #E6F5F4;
  }
  .resizer__grip {
      position: absolute;
      top: 50%;
      left: 50%;
      width: 2px;
      height: 32px;
      margin: -16px 0 0 -1px;
      border-radius: 2px;
      background: #CFD8E3;
      pointer-events: none;
  }
  .resizer:hover .resizer__grip {
      background: #0D9488;
  }
  .container__right {
      position: relative;
      flex: 1;
      min-width: 0;
      display: flex;
      flex-direction: column;
      overflow: hidden;
  }
</style>


// WEBPACK FOOTER //
// createOrUpdateConversation.vue?41169b51